/**
 * Grid Configuration Constants
 * Following Single Responsibility Principle - centralized grid configuration
 * Note: Using full class names because Tailwind doesn't support dynamic class construction
 */

/**
 * Generates responsive grid classes based on column count
 * @param {number} column - Number of columns to display
 * @returns {string} Tailwind CSS grid classes
 */
export const gridGenerator = (column) => {
  switch (column) {
    case 1:
      return "grid grid-cols-1 gap-4 sm:gap-6";
    case 2:
      return "grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2";
    case 3:
      return "grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3";
    case 4:
      return "grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4";
    case 5:
      return "grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5";
    case 6:
      return "grid grid-cols-2 gap-4 sm:gap-6 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6";
    default:
      // Default: 3 columns on large screens
      return "grid grid-cols-1 gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3";
  }
};

/**
 * Caption Label Configuration
 * Responsive text sizing based on column count
 */
const CAPTION_LABEL_CONFIG = {
  base: "font-semibold flex text-slate-600 dark:text-slate-400 items-end",
  sizes: {
    1: "text-sm sm:text-base pb-2",
    2: "text-sm sm:text-base pb-2",
    3: "text-xs sm:text-sm pb-2",
    4: "text-xs sm:text-sm pb-2",
    5: "text-xs pb-1",
    6: "text-xs pb-1",
  },
  default: "text-xs sm:text-sm pb-2",
};

export const captionLabelClassGenerator = (column) => {
  const sizeClass = CAPTION_LABEL_CONFIG.sizes[column] || CAPTION_LABEL_CONFIG.default;
  return `${CAPTION_LABEL_CONFIG.base} ${sizeClass}`;
};

/**
 * Image Height Configuration
 * Responsive image heights with smooth transitions across breakpoints
 */
const IMAGE_HEIGHT_CONFIG = {
  1: "h-[200px] sm:h-[270px] md:h-[320px] lg:h-[370px]",
  2: "h-[180px] sm:h-[200px] md:h-[220px] lg:h-[240px]",
  3: "h-[160px] sm:h-[180px] md:h-[220px] lg:h-[250px]",
  4: "h-[140px] sm:h-[160px] md:h-[170px] lg:h-[180px]",
  5: "h-[100px] sm:h-[110px] md:h-[115px] lg:h-[120px]",
  6: "h-[80px] sm:h-[90px] md:h-[95px] lg:h-[100px]",
};

const DEFAULT_IMAGE_HEIGHT = "h-[180px] sm:h-[220px] md:h-[280px] lg:h-[320px]";

export const imageClassGenerator = (column) => {
  return IMAGE_HEIGHT_CONFIG[column] || DEFAULT_IMAGE_HEIGHT;
};

/**
 * Heading Configuration
 * Responsive heading sizes with proper spacing
 */
const HEADING_CONFIG = {
  1: "text-base sm:text-lg md:text-xl font-bold py-3 sm:py-4 md:py-6 line-clamp-2",
  2: "text-base sm:text-lg md:text-xl font-semibold py-3 sm:py-4 md:py-6 line-clamp-2",
  3: "text-sm sm:text-base md:text-lg font-bold py-2 sm:py-3 md:py-4 line-clamp-2",
  4: "text-xs sm:text-sm md:text-base font-bold py-2 sm:py-3 line-clamp-2",
  5: "text-xs sm:text-sm font-bold py-2 sm:py-3 line-clamp-2",
  6: "text-xs sm:text-sm font-bold py-2 sm:py-3 line-clamp-2",
};

const DEFAULT_HEADING = "text-base sm:text-lg md:text-xl font-bold py-3 sm:py-4 md:py-6 line-clamp-2";

export const headingClassGenerator = (column) => {
  return HEADING_CONFIG[column] || DEFAULT_HEADING;
};

/**
 * Padding Configuration
 * Responsive padding based on column count and screen size
 */
const PADDING_CONFIG = {
  1: "px-2 sm:px-3 md:px-4",
  2: "px-2 sm:px-3 md:px-4",
  3: "px-2 sm:px-3",
  4: "px-1 sm:px-2",
  5: "px-1",
  6: "px-1",
};

const DEFAULT_PADDING = "px-2 sm:px-4 md:px-6 lg:px-8";

export const paddingClassGenerator = (column) => {
  return PADDING_CONFIG[column] || DEFAULT_PADDING;
};

/**
 * Section Text Configuration
 * Responsive text styling for content sections
 */
const SECTION_CONFIG = {
  base: "text-slate-600 dark:text-slate-400 line-clamp-3",
  sizes: {
    1: "text-sm sm:text-base py-2 sm:py-3 md:py-4",
    2: "text-sm sm:text-base py-2 sm:py-3",
    3: "text-xs sm:text-sm py-1 sm:py-2",
    4: "text-xs sm:text-sm py-1 sm:py-2",
    5: "text-xs py-1",
    6: "text-xs py-1",
  },
  default: "text-sm sm:text-base py-2 sm:py-3 md:py-4",
};

export const sectionClassGenerator = (column) => {
  const sizeClass = SECTION_CONFIG.sizes[column] || SECTION_CONFIG.default;
  return `${SECTION_CONFIG.base} ${sizeClass}`;
};

/**
 * Button Configuration
 * Responsive button styling with improved touch targets
 */
const BUTTON_CONFIG = {
  base: "text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg flex items-center justify-center transition-all duration-300 shadow-md hover:shadow-lg",
  sizes: {
    1: "text-sm sm:text-base gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3",
    2: "text-sm sm:text-base gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3",
    3: "text-xs sm:text-sm gap-2 px-2 sm:px-3 py-2",
    4: "text-xs sm:text-sm gap-2 px-2 sm:px-3 py-2",
    5: "text-xs gap-1 px-2 py-1 sm:py-2",
    6: "text-xs gap-1 px-2 py-1 sm:py-2",
  },
  default: "text-sm sm:text-base gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3",
};

export const buttonClassGenerator = (column) => {
  const sizeClass = BUTTON_CONFIG.sizes[column] || BUTTON_CONFIG.default;
  return `${BUTTON_CONFIG.base} ${sizeClass}`;
};

/**
 * Render Content
 * Safely renders HTML content
 * @param {number} column - Column count (reserved for future use)
 * @param {string} content - HTML content to render
 * @returns {JSX.Element} Rendered paragraph element
 */
export const renderContent = (column, content) => {
  return <p dangerouslySetInnerHTML={{ __html: content || '' }} />;
};

/**
 * Utility function to truncate text
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} Truncated text with ellipsis if needed
 */
export const truncateText = (text, maxLength = 150) => {
  if (!text) return '';
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};

/**
 * Card height configuration based on content type
 */
export const CARD_HEIGHTS = {
  books: "h-auto min-h-[320px] sm:min-h-[350px] md:min-h-[380px]",
  publications: "h-auto min-h-[380px] sm:min-h-[420px] md:min-h-[460px]",
  news: "h-auto min-h-[400px] sm:min-h-[450px] md:min-h-[500px]",
  events: "h-auto min-h-[400px] sm:min-h-[450px] md:min-h-[500px]",
  default: "h-auto min-h-[380px] sm:min-h-[420px] md:min-h-[460px]",
};

/**
 * Get card height class based on item type
 * @param {string} type - Item type (books, publications, news, events)
 * @returns {string} Tailwind height classes
 */
export const getCardHeightClass = (type) => {
  return CARD_HEIGHTS[type] || CARD_HEIGHTS.default;
};
